"use client"

import { useTheme } from "./theme-provider"

export default function BrainInsights({ insights, isLoading, moodTransition }) {
  const { theme } = useTheme()

  if (isLoading) {
    return (
      <div className="brain-insights loading">
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Analyzing brain activity...</h5>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!insights) {
    return null
  }

  return (
    <div className="brain-insights">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">AI Brain Analysis</h5>
          {moodTransition && (
            <div className="mood-transition-badge">
              Mood Transition: <span className="mood-from">{moodTransition.from}</span> →{" "}
              <span className="mood-to">{moodTransition.to}</span>
            </div>
          )}
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-primary">Brain Regions</h6>
              <ul className="list-group mb-3">
                {insights.brain_regions.map((region, index) => {
                  // Check if this region has a change in the mood transition
                  let changeDirection = null
                  if (moodTransition && moodTransition.regions_activation) {
                    const regionChange = moodTransition.regions_activation.find((r) => r.region === region)
                    if (regionChange) {
                      changeDirection = regionChange.change
                    }
                  }

                  return (
                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      {region}
                      <div>
                        {changeDirection && (
                          <span className={`badge me-2 bg-${changeDirection === "increasing" ? "success" : "danger"}`}>
                            {changeDirection === "increasing" ? "↑" : "↓"}
                          </span>
                        )}
                        <span className="badge bg-primary rounded-pill">
                          <i className="bi bi-brain"></i>
                        </span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
            <div className="col-md-6">
              <h6 className="text-success">Cognitive Effects</h6>
              <ul className="list-group mb-3">
                {insights.cognitive_effects.map((effect, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    {effect}
                    <span className="badge bg-success rounded-pill">
                      <i className="bi bi-lightning"></i>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-6">
              <h6 className="text-danger">Damage Risks</h6>
              <ul className="list-group mb-3">
                {insights.damage_risks.map((risk, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    {risk}
                    <span className="badge bg-danger rounded-pill">
                      <i className="bi bi-exclamation-triangle"></i>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-md-6">
              <h6 className="text-info">Stress Level</h6>
              <div className="alert alert-info">
                <div className="d-flex align-items-center">
                  <i className={`bi ${insights.stress_level === "relaxing" ? "bi-heart" : "bi-heart-pulse"} me-2`}></i>
                  <span className="text-capitalize">{insights.stress_level}</span>
                </div>
              </div>

              {/* Neurotransmitter changes for mood transitions */}
              {moodTransition && moodTransition.neurotransmitters && (
                <div className="mt-3">
                  <h6 className="text-primary">Neurotransmitter Changes</h6>
                  <ul className="list-group">
                    {moodTransition.neurotransmitters.map((nt, index) => (
                      <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        {nt.name}
                        <span className={`badge bg-${nt.change === "increasing" ? "success" : "danger"}`}>
                          {nt.change === "increasing" ? "↑" : "↓"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
